<template>
    <v-dialog
        :value="isOpen"
        scrollable
        max-width="650px"
        transition="slide-y-transition"
        eager
        @keydown.esc="onClose"
        @click:outside="onClose"
    >
        <v-card>
            <v-card-title>
                <h1 class="text-subtitle-1">
                    <template v-if="isReadyToPrint && !isEditable || showOnly">
                        Pakowanie - <strong>{{ packageModel.boxTypeName }}</strong>
                    </template>
                    <template v-else>
                        <template v-if="isNewPackage">
                            Dodaj pakowanie
                        </template>
                        <template v-else>
                            Edycja pakowania
                        </template>
                    </template>
                </h1>
            </v-card-title>

            <v-card-text>
                <div class="mx-5 my-1">
                    <v-row>
                        <v-col cols="12" sm="5">
                            <v-select
                                v-model="packageModel.boxType"
                                :items="boxTypes"
                                label="Sposób pakowania"
                                item-text="name"
                                return-object
                                outlined
                                dense
                                :disabled="!isEditable || showOnly"
                                :error-messages="packageBoxTypeErrors"
                                @change="changeBoxType"
                            />
                        </v-col>
                        <v-col cols="12" sm="7">
                            <v-autocomplete
                                v-model="packageModel.packageType"
                                label="Opakowanie"
                                :items="availablePackages"
                                item-text="name"
                                return-object
                                outlined
                                dense
                                :disabled="!isEditable || showOnly"
                                :error-messages="packagePackageTypeErrors"
                                @change="changePackageType"
                            />
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" sm="4">
                            <v-text-field
                                v-model="packageModel.length"
                                label="Długość"
                                suffix="mm"
                                outlined
                                dense
                                :disabled="!isEditable || showOnly"
                                :readonly="!isCustomMadePallet && !isCustomMadeCardBoard"
                                :error-messages="packageLengthErrors"
                            />
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-text-field
                                v-model="packageModel.width"
                                label="Szerokość"
                                suffix="mm"
                                outlined
                                dense
                                :disabled="!isEditable || showOnly"
                                :readonly="!isCustomMadePallet && !isCustomMadeCardBoard"
                                :error-messages="packageWidthErrors"
                            />
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-text-field
                                v-model="packageModel.height"
                                label="Wysokość"
                                suffix="mm"
                                outlined
                                dense
                                :disabled="!isEditable || showOnly"
                                :readonly="!isCustomMadeCardBoard"
                                :error-messages="packageHeightErrors"
                            />
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" sm="6">
                            <v-text-field
                                v-model="packageModel.packageWeight"
                                label="Waga palety/opakowania"
                                type="number"
                                step="0.005"
                                suffix="kg"
                                :min="0.005"
                                outlined
                                dense
                                :disabled="!isEditable || showOnly"
                                :error-messages="packageWeightErrors"
                            />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field
                                v-model="packageModel.maxWeightCapacity"
                                label="Nośność opakowania"
                                type="number"
                                step="0.1"
                                suffix="kg"
                                :min="0.1"
                                outlined
                                dense
                                :disabled="!isEditable || showOnly"
                            />
                        </v-col>
                    </v-row>
                    <v-row v-if="isNewPackage">
                        <v-col cols="12" sm="3">
                            <v-tooltip :open-delay="tooltipDelay" top>
                                <template #activator="{on}">
                                    <v-text-field
                                        v-model="packageModel.elementsAmount"
                                        label="ilość elementów"
                                        min="1"
                                        type="number"
                                        outlined
                                        dense
                                        :disabled="!isEditable || showOnly"
                                        v-on="on"
                                    />
                                </template>
                                <span> Ilość opakowań o tych samych parametrach</span>
                            </v-tooltip>
                        </v-col>
                    </v-row>
                </div>
            </v-card-text>

            <v-divider />

            <v-card-actions>
                <v-btn
                    color="error"
                    text
                    data-sentry-label="Zamknij"
                    @click="onClose"
                >
                    Anuluj
                </v-btn>
                <v-spacer />
                <v-btn
                    v-if="!isReadyToPrint || isEditable && !showOnly"
                    color="success"
                    text
                    :data-sentry-label="isNewPackage ? 'Dodaj' : 'Zapisz'"
                    @click="onSave"
                >
                    <template v-if="isNewPackage">
                        Dodaj
                    </template>
                    <template v-else>
                        Zapisz
                    </template>
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>
<script>
import packages from '@/mixins/packages'
import { required, minValue } from 'vuelidate/lib/validators'
import { isEmpty } from 'lodash'
import tooltipDelay from '@/mixins/tooltip'
import parse from '@/mixins/parse'

export default {
    name: 'ShippingLabelPackageDialog',
    mixins: [
        packages,
        tooltipDelay,
        parse
    ],
    props: {
        isOpen: {
            type: Boolean
        },
        isEditable: {
            type: Boolean
        },
        action: {
            type: String,
            default: 'edit',
        },
        shippingLabePackage: {
            type: Object,
            default: () => {},
        },
        isReadyToPrint: {
            type: Boolean
        }
    },
    data() {
        return {
            packageModel: {},
            hover: false,
            availablePackages: []
        }
    },
    validations() {
        const rules = {
            packageModel: {
                boxTypeId: {
                    required,
                },
                packageTypeId: {
                    required,
                },
                packageWeight: {
                    required,
                    minValue: minValue(0.005),
                },
                maxWeightCapacity: {
                    minValue: minValue(0.01),
                }
            }
        }

        if (this.isCustomMadePallet || this.isCustomMadeCardBoard) {
            rules.packageModel['length'] = {
                required,
                minValue: minValue(1)
            }

            rules.packageModel['width'] = {
                required,
                minValue: minValue(1)
            }
        }
        if (this.isCustomMadeCardBoard) {
            rules.packageModel['height'] = {
                required,
                minValue: minValue(1)
            }
        }

        return rules
    },
    computed: {
        showOnly() {
            return this.action === 'show'
        },
        isNewPackage() {
            return this.action === 'create'
        },
        isCustomMadePallet() {
            return this.packageModel.packageTypeId === 34
        },
        isCustomMadeCardBoard() {
            return this.packageModel.packageTypeId === 46
        },
        filteredPackageTypes() {
            return this.packageTypes.filter(item =>
                item.boxType.id === this.packageModel.boxTypeId &&
                (
                    (this.packageModel.packageTypeId !== null && item.id === this.packageModel.packageTypeId) ||
                    item.isVisible
                )
            )
        },
        // Walidacje
        packageBoxTypeErrors() {
            const errors = []

            if (!this.$v.packageModel.boxTypeId.$dirty) return errors
            !this.$v.packageModel.boxTypeId.required && errors.push('Proszę podać sposób pakowania.')

            return errors
        },
        packagePackageTypeErrors() {
            const errors = []

            if (!this.$v.packageModel.packageTypeId.$dirty) return errors
            !this.$v.packageModel.packageTypeId.required && errors.push('Proszę podać rodzaj opakowania.')

            return errors
        },
        packageLengthErrors() {
            const errors = []

            if (!this.isCustomMadePallet && !this.isCustomMadeCardBoard) return errors

            if (!this.$v.packageModel.length.$dirty) return errors

            if (!this.$v.packageModel.length.required || !this.$v.packageModel.length.minValue) {
                errors.push('Proszę podać długość.')
            }

            return errors
        },
        packageWidthErrors() {
            const errors = []

            if (!this.isCustomMadePallet && !this.isCustomMadeCardBoard) return errors

            if (!this.$v.packageModel.width.$dirty) return errors

            if (!this.$v.packageModel.width.required || !this.$v.packageModel.width.minValue) {
                errors.push('Proszę podać szerokość.')
            }

            return errors
        },
        packageHeightErrors() {
            const errors = []

            if (!this.isCustomMadeCardBoard) return errors

            if (!this.$v.packageModel.height.$dirty) return errors

            if (!this.$v.packageModel.height.required || !this.$v.packageModel.height.minValue) {
                errors.push('Proszę podać wysokość.')
            }

            return errors
        },
        packageWeightErrors() {
            const errors = []

            if (!this.$v.packageModel.packageWeight.$dirty) return errors

            if (!this.$v.packageModel.packageWeight.required || !this.$v.packageModel.packageWeight.minValue) {
                errors.push('Proszę podać wagę.')
            }

            return errors
        },
    },
    watch: {
        async isOpen(value) {
            if (value) {
                this.setPackageModel(this.shippingLabePackage)
                await this.getPackageBoxTypes(true)
                await this.getPackagesTypes(true)
                this.setAvailablePackages()
            }
        },
    },
    methods: {
        changeBoxType(boxType) {
            this.packageModel.boxTypeId = boxType.id
            this.packageModel.boxTypeName = boxType.name
            this.packageModel.packageType = null
            this.packageModel.packageTypeId = null
            this.packageModel.setManuallyMaxWeightCapacity = false
            this.packageModel.maxWeightCapacity = 0.0
            this.packageModel.packageWeight = 0.0
            this.setAvailablePackages()
        },
        changePackageType(packageType) {
            this.packageModel.packageTypeId = packageType.id
            this.packageModel.packageTypeName = packageType.name
            this.packageModel.length = packageType.length
            this.packageModel.width = packageType.width
            this.packageModel.height = packageType.height
            this.packageModel.maxWeightCapacity = packageType.maxWeightCapacity
            this.packageModel.setManuallyMaxWeightCapacity = false
            this.packageModel.packageWeight = +packageType.weight
            this.setAvailablePackages()
        },
        setAvailablePackages() {
            // available means all "visible" + selected (if selected and invisible)
            const visible = this.filteredPackageTypes.filter(item => item.isVisible === true)
            const selected = this.packageModel.packageType

            this.availablePackages = [...visible]

            if (selected && selected.isVisible === false) {
                const exists = this.availablePackages.some(p => p.id === selected.id)

                if (!exists) this.availablePackages.push(selected)
            }
        },
        setPackageModel(value = {}) {
            this.packageModel = !isEmpty(value) ? value : {
                boxTypeId: null,
                packageTypeName: null,
                packageTypeId: null,
                boxType: null,
                packageType: null,
                length: null,
                width: null,
                height: null,
                weight: null,
                packageWeight: null,
                maxWeightCapacity: null,
                elementsAmount: 1
            }
        },
        onSave() {
            this.$v.$touch()

            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })

                return
            }

            let payload

            if (this.isNewPackage) {
                payload = []

                let payloadPackage = {...this.packageModel}

                payloadPackage.shippingOrderPositionPackageGoods = []

                if (this.packageModel.elementsAmount > 0) {
                    for (let i = 0; i < parseInt(this.packageModel.elementsAmount); i++) {
                        payload.push({ ...payloadPackage })
                    }
                }
            } else {
                payload = {...this.packageModel}
            }

            this.$emit('on-save', payload)
            this.onClose()
        },
        onClose() {
            this.$emit('on-close')
            this.$v.$reset()
        }
    },
}
</script>